import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  FitnessCenter as FitnessIcon,
  PlayArrow as PlayIcon,
  Timer as TimerIcon,
  TrendingUp as DifficultyIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface MasterDrill {
  id: string;
  drillCode: string;
  drillName: string;
  drillType?: string;
  description?: string;
  durationMinutes?: number;
  equipmentNeeded?: string[];
  setupInstructions?: string;
  executionSteps?: string[];
  coachingPoints?: string[];
  referenceVideos?: string[];
  difficultyLevel?: number;
}

const PlayerDrillsPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sport = searchParams.get('sport') || 'Badminton';
  const level = searchParams.get('level') || 'beginner';

  const [drills, setDrills] = useState<MasterDrill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDrill, setSelectedDrill] = useState<MasterDrill | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>(level);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const loadDrills = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-drills?sport=${sport}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        // Filter by level if not 'all' (for now, show all - level filtering will be based on mappings later)
        let filteredDrills = data.data || [];
        
        // TODO: Filter by skill level based on player_skill_levels and skill_level_drill_mapping
        // For MVP, show all active drills
        filteredDrills = filteredDrills.filter((d: MasterDrill) => true); // All drills for now
        
        setDrills(filteredDrills);
      } else {
        setError(data.message || 'Failed to load drills');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load drills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrills();
  }, [sport, selectedLevel]);

  const handleViewDetail = (drill: MasterDrill) => {
    setSelectedDrill(drill);
    setDetailDialogOpen(true);
  };

  const handleLevelChange = (newLevel: string) => {
    setSelectedLevel(newLevel);
    navigate(`/player/drills?sport=${sport}&level=${newLevel}`);
  };

  const getDifficultyColor = (level?: number) => {
    if (!level) return 'default';
    if (level >= 4) return 'error';
    if (level >= 3) return 'warning';
    return 'success';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Drills - {sport}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Practice drills to improve your skills
        </Typography>
      </Box>

      {/* Level Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Skill Level</InputLabel>
          <Select
            value={selectedLevel}
            label="Skill Level"
            onChange={(e) => handleLevelChange(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : drills.length === 0 ? (
        <Alert severity="info">
          No drills available for {sport} at {selectedLevel === 'all' ? 'any' : selectedLevel} level.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {drills.map((drill) => (
            <Grid item xs={12} sm={6} md={4} key={drill.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                      {drill.drillName}
                    </Typography>
                    <Chip
                      label={`Level ${drill.difficultyLevel || 1}`}
                      size="small"
                      color={getDifficultyColor(drill.difficultyLevel)}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Code: {drill.drillCode}
                  </Typography>
                  {drill.drillType && (
                    <Chip label={drill.drillType} size="small" sx={{ mt: 1, mr: 1 }} />
                  )}
                  {drill.description && (
                    <Typography variant="body2" sx={{ mt: 2 }} noWrap>
                      {drill.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {drill.durationMinutes && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimerIcon fontSize="small" color="action" />
                        <Typography variant="caption">{drill.durationMinutes} min</Typography>
                      </Box>
                    )}
                  </Box>
                  {drill.equipmentNeeded && drill.equipmentNeeded.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Equipment: {drill.equipmentNeeded.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={() => handleViewDetail(drill)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Drill Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedDrill?.drillName}</DialogTitle>
        <DialogContent>
          {selectedDrill && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Code: {selectedDrill.drillCode}
              </Typography>
              {selectedDrill.drillType && (
                <Chip label={selectedDrill.drillType} size="small" sx={{ mt: 1, mb: 2 }} />
              )}
              {selectedDrill.description && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Description</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{selectedDrill.description}</Typography>
                </>
              )}
              {selectedDrill.setupInstructions && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Setup Instructions</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{selectedDrill.setupInstructions}</Typography>
                </>
              )}
              {selectedDrill.executionSteps && selectedDrill.executionSteps.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Execution Steps</Typography>
                  <List>
                    {selectedDrill.executionSteps.map((step, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${index + 1}. ${step}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              {selectedDrill.coachingPoints && selectedDrill.coachingPoints.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Coaching Points</Typography>
                  <List>
                    {selectedDrill.coachingPoints.map((point, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={point} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              {selectedDrill.referenceVideos && selectedDrill.referenceVideos.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Reference Videos</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedDrill.referenceVideos.map((url, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        href={url}
                        target="_blank"
                        sx={{ mr: 1, mb: 1 }}
                      >
                        Video {index + 1}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => {
            // TODO: Link to practice/evaluation when implemented
            setDetailDialogOpen(false);
          }}>
            Start Practice
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlayerDrillsPage;

